import React, { useMemo, useState } from 'react';
import type {
  CmpCompetitor,
  CmpSourceFilter,
  CmpTone,
} from '../../growth-competitors/types';
import {
  CMP_COMPETITORS,
  CMP_SCAN_META,
  CMP_STATS,
} from '../../growth-competitors/mockData';
import {
  CMP_TYPE_STYLES,
  GROWTH_COMPETITORS_META,
} from '../../growth-competitors/config';
import { Icon } from '../Icon/Icon';
import { Pagination } from '../Pagination/Pagination';
import { Segmented } from '../Segmented/Segmented';
import './growth-competitors.css';

const TONE: Record<CmpTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

/** Threat ring colour: high = terracotta, medium = gold, low = success. */
function threatTone(score: number): CmpTone {
  if (score >= 70) return 'terra';
  if (score >= 50) return 'gold';
  return 'success';
}

/** Distance pill tone: closer competitors are a bigger threat. */
function distanceTone(miles: number): CmpTone {
  if (miles <= 2) return 'terra';
  if (miles <= 5) return 'gold';
  return 'success';
}

/** Active-ad emphasis: heavier spenders read hotter. */
function adsTone(activeAds: number): CmpTone {
  if (activeAds >= 8) return 'terra';
  if (activeAds >= 4) return 'gold';
  return 'neutral';
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('');
}

function CmpCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function CmpStatTile({
  label,
  value,
  delta,
  tone,
}: {
  label: string;
  value: string;
  delta: string;
  tone: CmpTone;
}) {
  return (
    <CmpCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{label}</div>
      <div className="mt-2 font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em] text-[var(--ink-0)]">
        {value}
      </div>
      <div
        className="mt-2 text-[12.5px]"
        style={{ color: tone === 'neutral' ? 'var(--ink-3)' : TONE[tone].accent }}
      >
        {delta}
      </div>
    </CmpCard>
  );
}

function CmpThreatRing({ score }: { score: number }) {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, score));
  const offset = circumference - (clamped / 100) * circumference;
  const accent = TONE[threatTone(score)].accent;

  return (
    <div className="relative h-10 w-10">
      <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
        <circle cx="20" cy="20" r={radius} fill="none" stroke="var(--surface-mute)" strokeWidth="3" />
        <circle
          cx="20"
          cy="20"
          r={radius}
          fill="none"
          stroke={accent}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 20 20)"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[13px] font-bold text-[var(--ink-0)]">
        {score}
      </span>
    </div>
  );
}

function CmpDistancePill({ miles }: { miles: number }) {
  const tone = TONE[distanceTone(miles)];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-[var(--r-pill)] px-2.5 py-1 text-[12px] font-semibold"
      style={{
        background: tone.tint,
        color: tone.accent,
        border: `1px solid color-mix(in srgb, ${tone.accent} 30%, transparent)`,
      }}
    >
      <Icon kind="pin" size={12} stroke={2.2} />
      {miles} mi
    </span>
  );
}

function CmpTypeChip({ type }: { type: CmpCompetitor['type'] }) {
  const tone = TONE[CMP_TYPE_STYLES[type].tone];
  return (
    <span
      className="inline-flex items-center rounded-[6px] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em]"
      style={{
        background: tone.tint,
        color: tone.accent,
        border: `1px solid color-mix(in srgb, ${tone.accent} 30%, transparent)`,
      }}
    >
      {type}
    </span>
  );
}

function CmpMoatBadge({ moat }: { moat: string }) {
  const tone = TONE.gold;
  return (
    <span
      title={moat}
      className="inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 text-[10px] font-bold"
      style={{
        background: tone.tint,
        color: tone.accent,
        border: `1px solid color-mix(in srgb, ${tone.accent} 30%, transparent)`,
      }}
    >
      <Icon kind="shield" size={11} stroke={2} />
      {moat}
    </span>
  );
}

function shortenKeywordLabel(keyword: string): string {
  const core = (keyword.split(' - ')[0] ?? keyword).trim();
  if (core.length <= 22) return core;
  return `${core.slice(0, 20)}…`;
}

function CmpRow({ row, onViewDetails }: { row: CmpCompetitor; onViewDetails?: (id: string) => void }) {
  const visibleKeywords = row.topKeywords.slice(0, 2).map(shortenKeywordLabel);
  const hiddenKeywordCount = Math.max(0, row.topKeywords.length - visibleKeywords.length);

  return (
    <article className="cmp-row cmp-table-grid border-b border-[var(--cream-2)] transition-colors last:border-b-0 hover:bg-[var(--surface-mute)]">
      {/* Competitor: avatar + name + domain + chips */}
      <div className="cmp-cell-primary flex min-w-0 items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[var(--cream-2)] bg-[var(--success-tint)] font-[var(--font-display)] text-[13px] font-bold text-[var(--success)]">
          {initials(row.name)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-[14.5px] font-semibold text-[var(--ink-0)]">{row.name}</p>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="truncate font-[var(--font-mono)] text-[12px] text-[var(--ink-4)]">
              {row.domain}
            </span>
            {row.source === 'manual' ? (
              <span className="shrink-0 rounded-[var(--r-pill)] bg-[var(--info-tint)] px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-[var(--info)]">
                Manual
              </span>
            ) : null}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <CmpTypeChip type={row.type} />
            {row.moat ? <CmpMoatBadge moat={row.moat} /> : null}
          </div>
        </div>
      </div>

      {/* Distance */}
      <div className="cmp-cell-metric">
        <CmpDistancePill miles={row.distanceMiles} />
      </div>

      {/* Threat */}
      <div className="cmp-cell-metric">
        <CmpThreatRing score={row.threatScore} />
      </div>

      {/* Rating */}
      <div className="cmp-cell-metric flex items-center gap-1">
        <Icon kind="star" size={13} stroke={1.8} className="text-[var(--gold)]" />
        <span className="text-[15px] font-semibold text-[var(--ink-0)]">{row.rating}</span>
      </div>

      {/* Reviews */}
      <div className="cmp-cell-metric text-center">
        <p className="text-[15px] font-semibold text-[var(--ink-0)]">{row.reviews}</p>
        <p className="mt-0.5 text-[11px] text-[var(--ink-4)]">{row.reviewVelocity}</p>
      </div>

      {/* Active ads */}
      <div className="cmp-cell-metric flex items-center gap-1">
        <span
          className="text-[15px] font-semibold"
          style={{ color: TONE[adsTone(row.activeAds)].accent }}
        >
          {row.activeAds}
        </span>
        <span className="text-[11px] text-[var(--ink-4)]">ads</span>
      </div>

      {/* Top keywords */}
      <div className="cmp-cell-keywords min-w-0">
        {visibleKeywords.length ? (
          <div className="flex max-w-full flex-nowrap items-center gap-1.5 overflow-hidden">
            {visibleKeywords.map((keyword, index) => (
              <span
                key={`${keyword}-${index}`}
                title={row.topKeywords[index]}
                className="cmp-kw-pill rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--ink-3)]"
              >
                {keyword}
              </span>
            ))}
            {hiddenKeywordCount > 0 ? (
              <span
                title={row.topKeywords.slice(2).join('\n')}
                className="shrink-0 rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--ink-4)]"
              >
                +{hiddenKeywordCount}
              </span>
            ) : null}
          </div>
        ) : (
          <span className="text-[12px] text-[var(--ink-4)]">—</span>
        )}
      </div>

      {/* Action */}
      <div className="cmp-cell-action">
        <button
          type="button"
          onClick={() => onViewDetails?.(row.id)}
          className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent text-[13px] font-semibold text-[var(--ai-3)]"
        >
          View details
          <Icon kind="right" size={14} stroke={2.2} />
        </button>
      </div>
    </article>
  );
}

const FILTERS: { value: CmpSourceFilter; label: (counts: Record<CmpSourceFilter, number>) => string }[] = [
  { value: 'all', label: (c) => `All (${String(c.all)})` },
  { value: 'auto', label: (c) => `Auto-discovered (${String(c.auto)})` },
  { value: 'manual', label: (c) => `Manually Added (${String(c.manual)})` },
];

export interface AllCompetitorsViewProps {
  items?: CmpCompetitor[];
  stats?: typeof CMP_STATS;
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  loading?: boolean;
  onPageChange?: (page: number) => void;
  onAddCompetitor?: () => void;
  onViewDetails?: (id: string) => void;
}

export function AllCompetitorsView({
  items: itemsProp,
  stats: statsProp,
  pagination,
  loading = false,
  onPageChange,
  onAddCompetitor,
  onViewDetails,
}: AllCompetitorsViewProps = {}) {
  const meta = GROWTH_COMPETITORS_META;
  const controlled = itemsProp !== undefined;
  const sourceItems = controlled ? itemsProp : CMP_COMPETITORS;
  const stats = statsProp ?? CMP_STATS;
  const [filter, setFilter] = useState<CmpSourceFilter>('all');

  const counts = useMemo<Record<CmpSourceFilter, number>>(
    () => ({
      all: sourceItems.length,
      auto: sourceItems.filter((row) => row.source === 'auto').length,
      manual: sourceItems.filter((row) => row.source === 'manual').length,
    }),
    [sourceItems],
  );

  const rows = useMemo(
    () =>
      filter === 'all'
        ? sourceItems
        : sourceItems.filter((row) => row.source === filter),
    [filter, sourceItems],
  );

  const segmentOptions = useMemo(
    () => FILTERS.map((option) => ({ value: option.value, label: option.label(counts) })),
    [counts],
  );

  return (
    <div className="growth-competitors-root relative mx-auto w-full max-w-[1320px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px] flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="target" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>

        <button
          type="button"
          onClick={onAddCompetitor}
          className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-4 text-[13px] font-semibold text-white shadow-[var(--sh-1)]"
          style={{ background: 'var(--grad-ink)' }}
        >
          <Icon kind="plus" size={16} stroke={2.2} />
          Add Competitor
        </button>
      </div>

      <div
        className="cmp-kpi-grid mb-6 grid gap-3"
        style={{
          gridTemplateColumns: `repeat(${Math.min(stats.length, 5)}, minmax(0, 1fr))`,
        }}
      >
        {stats.map((stat) => (
          <CmpStatTile
            key={stat.key}
            label={stat.label}
            value={stat.value}
            delta={stat.delta}
            tone={stat.tone}
          />
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Segmented
          options={segmentOptions}
          value={filter}
          onChange={(value) => {
            setFilter(value as CmpSourceFilter);
          }}
        />
        <p className="text-[12.5px] text-[var(--ink-4)]">
          {CMP_SCAN_META.radius} · {CMP_SCAN_META.cadence}
        </p>
      </div>

      <CmpCard className="overflow-hidden">
        <div className="cmp-table-scroll">
          <div className="cmp-table-inner">
            <div className="cmp-table-grid cmp-table-header border-b border-[var(--cream-2)] bg-[var(--surface-mute)]">
              <span className={eyebrowClass}>Competitor</span>
              <span className={`${eyebrowClass} cmp-cell-metric`}>Distance ↑</span>
              <span className={`${eyebrowClass} cmp-cell-metric`}>Threat</span>
              <span className={`${eyebrowClass} cmp-cell-metric`}>Rating</span>
              <span className={`${eyebrowClass} cmp-cell-metric`}>Reviews</span>
              <span className={`${eyebrowClass} cmp-cell-metric`}>Active Ads</span>
              <span className={eyebrowClass}>Top Keywords</span>
              <span className="cmp-cell-action" aria-hidden="true" />
            </div>

            {rows.length ? (
              rows.map((row) => (
                <CmpRow
                  key={row.id}
                  row={row}
                  {...(onViewDetails ? { onViewDetails } : {})}
                />
              ))
            ) : (
              <div className="col-span-full px-5 py-11 text-center text-[13px] text-[var(--ink-3)]">
                No competitors match this filter.
              </div>
            )}
          </div>
        </div>
      </CmpCard>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <p className="inline-flex items-center gap-2 text-[12.5px] text-[var(--ink-3)]">
          <Icon kind="refresh" size={15} stroke={1.9} className="text-[var(--ai-3)]" />
          {CMP_SCAN_META.footerNote}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-[var(--font-mono)] text-[12px] text-[var(--ink-4)]">
            {pagination
              ? `${(pagination.page - 1) * pagination.pageSize + 1}–${Math.min(
                  pagination.page * pagination.pageSize,
                  pagination.totalCount,
                )} of ${pagination.totalCount}`
              : `Showing ${rows.length} of ${sourceItems.length}`}
          </span>
          {pagination && onPageChange && pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              total={pagination.totalPages}
              onChange={onPageChange}
              variant="compact"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
